import { ServiceFeePayerSchema } from '@/apis/payments/service_fee.schema'
import { z } from 'zod'

export interface GetPaymentDetailsArgs {
  payment_id: string
  program_id: number
  messages?: boolean
}

export const PaymentTypeSchema = z.number().transform((value) => {
  switch (value) {
    case 1:
      return 'Pay Provider'
    case 2:
      return 'Reimbursement'
    case 3:
      return 'Send Bill'
    case 4:
      return 'Marketplace'
  }

  return 'Unknown'
})

export const PaymentStatusSchema = z.number().transform((value) => {
  switch (value) {
    case 1:
      return 'Approved'
    case 2:
      return 'Declined'
    case 3:
      return 'In review - LEGACY DO NOT USE'
    case 4:
      return 'In review Admin'
    case 5:
      return 'In review Provider'
    case 6:
      return 'In review Applicant'
    case 7:
      return 'Appeal awaiting decision'
    case 8:
      return 'Declined by Admin'
    case 9:
      return 'Declined by Provider'
    case 10:
      return 'Declined by Applicant'
    case 11:
      return 'Refunded by Provider'
    case 12:
      return 'Appeal Declined by Admin'
    case 13:
      return 'Reconciliation Required'
    case 14:
      return 'Partially Refunded by Provider'
    default:
      return 'Unknown'
  }
})
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>

export const PurseHistorySchema = z.object({
  id: z.number().int().positive(),
  payment_id: z.number().int().positive(),
  purse_id: z.number().int().positive(),
  total_amount: z.number().positive(),
  name: z.string(),
  amount: z.number().positive(),
  created_datetime: z.string().datetime(),
  // Not always sent on a payment's `purse_movements`, and nothing reads it, so
  // a missing one must not take the whole payment detail down with it.
  last_datetime: z.string().datetime().optional(),
  student_name: z.string(),
})
export type PurseHistory = z.infer<typeof PurseHistorySchema>
export type PaymentItem = z.infer<typeof PurseHistorySchema>

export type GetApplicantPaymentsArgs = {
  program_id: number
  applicant_id: number
  page: number
  per_page: number
  search: string
}

// Define the Zod schema for payment update request
export const UpdatePaymentSchema = z.object({
  payment_id: z.number(),
  program_id: z.number(), // required
  status: z.number().optional(),
  provider_id: z.number().optional(),
  total_amount: z.number().optional(),
  service_fee: z.number().optional(),
  amount_provider: z.number().optional(),
  comments: z.string().optional(),
  po_number: z.string().optional(),
  tracking_number: z.string().nullable().optional(),
  purse: z
    .array(
      z.object({
        purse_id: z.number(),
        amount: z.number(),
      })
    )
    .optional(),
  global_expense_categories: z
    .array(
      z.object({
        id: z.number(),
        amount: z.number(),
      })
    )
    .optional(),
  files: z
    .array(
      z.object({
        document_type: z.number(),
        file_name: z.string(),
      })
    )
    .optional(),
})

export type UpdatePaymentDetailsArgs = z.infer<typeof UpdatePaymentSchema>

const ExpenseCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  amount: z.number(),
})

export const PaymentMessageSchema = z.object({
  id: z.number(),
  payment_id: z.number(),
  message: z.string(),
  created_datetime: z.string(),
  is_read: z.number(),
  user_id: z.number().nullable(),
  internal: z.number().nullable(),
})

export const PaymentSchema = z.object({
  payment_id: z.number(),
  status: PaymentStatusSchema,
  type: PaymentTypeSchema,
  transaction_type: z.string().nullable(),
  program_id: z.number(),
  student_name: z.array(z.string()).nullable(),
  applicant_name: z.string(),
  provider_id: z.number().nullable(),
  total_amount: z.number(),
  amount_provider: z.number(),
  service_fee: z.number(),
  service_fee_payer: ServiceFeePayerSchema,
  comments: z.string().nullable(),
  invoice_quote: z.string().nullable(),
  provider_name: z.string().nullable(),
  created_datetime: z.string(), // ISO date string
  expense_categories: z.array(ExpenseCategorySchema),
  messages: z.array(PaymentMessageSchema),
})

export const PaymentDocumentSchema = z.object({
  id: z.number(),
  global_spend_document_type_id: z.number(),
  payment_id: z.number(),
  path: z.string(),
  created_datetime: z.string().datetime(),
  last_datetime: z.string().datetime(),
  student_id: z.number().optional(),
  student_name: z.string().optional(),
  invoice_number: z.string().optional(),
  invoice_date: z.string().optional(),
  amount: z.number().optional(),
  notes: z.string().optional(),
})

export const PaymentItemSchema = z.object({
  id: z.number(),
  provider_item_id: z.number(),
  payment_id: z.number(),
  price: z.number(),
  quantity: z.number(),
  provider_item: z.object({
    image: z.string(),
    name: z.string(),
  }),
})

export const PaymentDetailSchema = z.object({
  application_id: z.number().or(z.undefined()),
  status: PaymentStatusSchema,
  type: PaymentTypeSchema,
  payment_id: z.number().optional().default(0),
  allowed_students: z.array(z.string()).nullable().optional(),
  transaction_type: z.string().optional(),
  program_id: z.number(),
  purse_movements: z.array(PurseHistorySchema),
  applicant_name: z.string().nullable().optional(),
  provider_id: z.number().nullable(),
  has_appealed: z.number().transform((value) => {
    if (value === 1) {
      return true
    }

    return false
  }),
  total_amount: z.number(),
  service_fee: z.number(),
  service_fee_payer: ServiceFeePayerSchema,
  amount_provider: z.number(),
  invoice_quote: z.string().nullable(),
  tracking_number: z.string().nullable().optional(),
  admin_review_comment: z.string().nullable(),
  applicant_review_comment: z.string().nullable(),
  provider_review_comment: z.string().nullable(),
  comments: z.string().nullable(),
  provider_name: z.string().nullable().optional(),
  store_name: z.string().nullable().optional(),
  created_datetime: z.string().datetime(),
  last_message: z.string().nullable(), // New field
  students: z
    .array(
      z.object({
        student_id: z.number(),
        student_name: z.string(),
      })
    )
    .optional(),
  messages: z.array(PaymentMessageSchema),
  expense_categories: z.array(ExpenseCategorySchema),
  payment_documents: z.array(PaymentDocumentSchema),
  payment_items: z.array(PaymentItemSchema),
})

export type PaymentType = z.infer<typeof PaymentTypeSchema>
export type PaymentDetail = z.infer<typeof PaymentDetailSchema>
export type PaymentItemInfo = z.infer<typeof PaymentItemSchema>
export type PaymentDocument = z.infer<typeof PaymentDocumentSchema>

export type Payment = z.infer<typeof PaymentSchema>
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>
export type PaymentMessage = z.infer<typeof PaymentMessageSchema>
